import type { IConnectorPermissionChangedNotificationData } from "@frontend-shared/types";
import { resolveConnectorPermissionChangedBody } from "@frontend-shared/utils";
import { ShieldCheck } from "lucide-react";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { NotificationCardShell } from "../NotificationCardShell";

interface IConnectorPermissionChangedCardProps {
  uuid: string;
  data: IConnectorPermissionChangedNotificationData;
  createdAt: string;
  isRead: boolean;
  onViewConnectorSettings: (provider: string, projectUuid: string) => void;
}

const ConnectorPermissionChangedCardComponent = ({
  uuid,
  data,
  createdAt,
  isRead,
  onViewConnectorSettings,
}: IConnectorPermissionChangedCardProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");
  // NOTE: Second namespace because the level names (and the "No minimum" org
  // wording) live in the connector-approval catalog, not the notifications one.
  // Both are handed to the shared body builder, which owns the copy decisions so
  // web and mobile can't drift.
  const { t: translateApproval } = useTranslation("connectorApproval");

  const body = useMemo(
    () =>
      resolveConnectorPermissionChangedBody({
        data,
        translate: t,
        translateApproval,
      }),
    [data, t, translateApproval],
  );

  // NOTE: Connectors are project-scoped, so the parent handler switches into
  // the project where this user's connection lives before opening
  // /integrations/:provider — same contract as the reconnect CTA.
  const handleViewSettings = useCallback((): void => {
    onViewConnectorSettings(data.provider, data.projectUuid);
  }, [data.projectUuid, data.provider, onViewConnectorSettings]);

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId="connector_permission_changed"
      createdAt={createdAt}
      isRead={isRead}
      icon={<ShieldCheck className="h-5 w-5 text-amber-500" />}
      iconBgClassName="bg-amber-500/10"
      ctaLabel={t("type.connector_permission_changed.cta_label")}
      onCtaClick={handleViewSettings}
      body={body}
    />
  );
};

export const ConnectorPermissionChangedCard = memo(
  ConnectorPermissionChangedCardComponent,
);
